// RosterEditor — promoted from learner-app/pages/coach/RostersPage
// to @flowgenix/shared-ui (Phase 2 / Step C-3.3 Pass 2 Batch A).
//
// Renderer for the `roster_editor` widget kind. Coach-side learner
// roster management surface: invite/remove learners, create rosters
// for a domain, delete rosters.
//
// API clients are injected via props so the widget is decoupled from
// learner-app's `services/api` singleton. Atlas Catalog and any other
// embedding host can pass a mock API for previews. Confirm/toast
// helpers also flow through props (default to safe no-ops) so the
// widget renders without requiring the `<OverlayHost/>` to be mounted.
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const NOOP_API = Object.freeze({});

async function noopConfirm() {
  if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
    return window.confirm('Delete?');
  }
  return true;
}

const noopToast = Object.freeze({
  info: () => {},
  success: () => {},
  error: () => {},
  warn: () => {},
});

/**
 * RosterEditor
 *
 * Props:
 *   rosterAPI       — required; must expose:
 *                       list(): Promise<{data:{rosters:[]}}>
 *                       get(rosterId): Promise<{data:Roster}>
 *                       create({name, domain_id, description}): Promise<{data:Roster}>
 *                       remove(rosterId): Promise<unknown>
 *                       addLearners(rosterId, {learner_ids:[]}): Promise<{data:Roster}>
 *                       removeLearner(rosterId, learnerId): Promise<unknown>
 *   coachAPI        — required; must expose listLearners(): Promise<{data:{learners:[]}}>
 *   confirm         — async confirm({title, message, ...}) => boolean (defaults to window.confirm)
 *   toast           — { info, success, error, warn } object (defaults to no-op)
 *   defaultDomainId — pre-fills the create form's domain_id ('football' by default)
 *   domainOptions   — [{id, label}] for the create-form domain dropdown
 */
export default function RosterEditor({
  rosterAPI = NOOP_API,
  coachAPI = NOOP_API,
  confirm = noopConfirm,
  toast = noopToast,
  defaultDomainId = 'football',
  domainOptions,
}) {
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [allLearners, setAllLearners] = useState([]);

  const reload = useCallback(async () => {
    if (!rosterAPI?.list) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await rosterAPI.list();
      setRosters(data?.rosters || []);
    } catch (_) {
      setRosters([]);
    } finally {
      setLoading(false);
    }
  }, [rosterAPI]);

  useEffect(() => {
    reload();
    if (coachAPI?.listLearners) {
      coachAPI
        .listLearners()
        .then((r) => setAllLearners(r?.data?.learners || []))
        .catch(() => setAllLearners([]));
    }
  }, [reload, coachAPI]);

  const openRoster = async (id) => {
    if (!rosterAPI?.get) return;
    try {
      const { data } = await rosterAPI.get(id);
      setSelected(data);
    } catch (_) {
      /* leave previous selection alone */
    }
  };

  const handleAddLearner = async (learnerId) => {
    if (!selected || !rosterAPI?.addLearners) return;
    const { data } = await rosterAPI.addLearners(selected.roster_id, { learner_ids: [learnerId] });
    openRoster(data.roster_id);
    reload();
  };

  const handleRemoveLearner = async (learnerId) => {
    if (!selected || !rosterAPI?.removeLearner) return;
    const { data } = await rosterAPI.removeLearner(selected.roster_id, learnerId);
    openRoster(data.roster_id);
    reload();
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete roster?',
      message: 'Delete this roster? Learners are not deleted, only the grouping.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    if (!rosterAPI?.remove) return;
    await rosterAPI.remove(id);
    toast.info?.('Roster deleted');
    if (selected?.roster_id === id) setSelected(null);
    reload();
  };

  const availableLearners = useMemo(
    () => (allLearners || []).filter((l) => !selected?.learner_ids?.includes(l.learner_id)),
    [allLearners, selected],
  );

  return (
    <div className="rosters-page">
      <div className="rosters-list">
        <div className="rosters-list-header">
          <strong>My rosters</strong>
          <button className="btn-primary btn-sm" onClick={() => setShowCreate(true)}>+ New</button>
        </div>
        {loading && <div>Loading…</div>}
        {rosters.length === 0 && !loading && (
          <div className="rosters-empty">
            No rosters yet. Create one to assign tutorials and plays to a group of learners.
          </div>
        )}
        {rosters.map((r) => (
          <div
            key={r.roster_id}
            className={`roster-row ${selected?.roster_id === r.roster_id ? 'active' : ''}`}
            onClick={() => openRoster(r.roster_id)}
          >
            <div className="roster-row-name">{r.name}</div>
            <div className="roster-row-meta">{r.learner_count} learner{r.learner_count === 1 ? '' : 's'} · {r.domain_id}</div>
          </div>
        ))}
      </div>

      <div className="roster-detail">
        {!selected ? (
          <div className="roster-detail-empty">Select a roster on the left, or create a new one.</div>
        ) : (
          <>
            <div className="roster-detail-header">
              <h3>{selected.name}</h3>
              <div>
                <button className="btn-warning btn-sm" onClick={() => handleDelete(selected.roster_id)}>Delete roster</button>
              </div>
            </div>
            <p>{selected.description}</p>
            <div className="roster-section">
              <h4>Learners ({(selected.learners || []).length})</h4>
              {(selected.learners || []).map((l) => (
                <div className="roster-learner-row" key={l.learner_id}>
                  <span><strong>{l.display_name || l.email}</strong> {l.email && <em>({l.email})</em>}</span>
                  <button className="btn-ghost btn-sm" onClick={() => handleRemoveLearner(l.learner_id)}>Remove</button>
                </div>
              ))}
              {(selected.learners || []).length === 0 && <div className="roster-empty">No learners yet.</div>}
            </div>

            <div className="roster-section">
              <h4>Add learners</h4>
              {availableLearners.length === 0 ? (
                <div className="roster-empty">All your learners are already on this roster.</div>
              ) : (
                <div className="roster-add-list">
                  {availableLearners.map((l) => (
                    <button
                      key={l.learner_id}
                      className="btn-ghost btn-sm"
                      onClick={() => handleAddLearner(l.learner_id)}
                    >
                      + {l.display_name || l.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showCreate && (
        <CreateRosterModal
          rosterAPI={rosterAPI}
          defaultDomainId={defaultDomainId}
          domainOptions={domainOptions}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reload(); }}
        />
      )}
    </div>
  );
}

RosterEditor.kind = 'roster_editor';
RosterEditor.displayName = 'RosterEditor';

function CreateRosterModal({ rosterAPI, defaultDomainId, domainOptions, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [domainId, setDomainId] = useState(defaultDomainId || 'football');
  const [description, setDescription] = useState('');

  const options = domainOptions && domainOptions.length > 0
    ? domainOptions
    : [{ id: 'football', label: 'Football' }];

  const handleCreate = async () => {
    if (!rosterAPI?.create) return;
    await rosterAPI.create({ name, domain_id: domainId, description });
    onCreated();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3>Create roster</h3>
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Varsity QBs 2026" /></label>
        <label>Domain
          <select value={domainId} onChange={(e) => setDomainId(e.target.value)}>
            {options.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label>Description<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></label>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleCreate} disabled={!name}>Create</button>
        </div>
      </div>
    </div>
  );
}
